/**
 * @fileoverview Tenant Activity API calls.
 * @description Wraps GET /sales/tenants/:id/activities and POST /sales/tenants/:id/activities —
 * the one endpoint pair both "Tenant Activities" and View Tenant's activities tab are meant to read
 * from. New, additive API surface — no hook calls this yet.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

import type { Activity } from '@/features/sales/types/tenant.types'

export interface AddActivityRequest {
  type: Activity['type']
  details: string
  scheduledAt?: string | null
  attachments?: string[]
  authorId: string
}

/**
 * @description Fetches a tenant's full activity history, oldest first.
 * @param {string} tenantId - The tenant whose activities to fetch.
 * @returns {Promise<Activity[]>} The tenant's activities, in recorded order.
 */
export async function getTenantActivities(tenantId: string): Promise<Activity[]> {
  const { data } = await httpClient.get<Activity[]>(`/sales/tenants/${tenantId}/activities`)
  return data
}

/**
 * @description Records a new activity against a tenant. There is no corresponding update call —
 * saved activities cannot be edited, only superseded by a new entry.
 * @param {string} tenantId - The tenant the activity is recorded against.
 * @param {AddActivityRequest} input - The activity's details and author.
 * @returns {Promise<Activity>} The created activity.
 */
export async function addTenantActivity(
  tenantId: string,
  input: AddActivityRequest,
): Promise<Activity> {
  const { data } = await httpClient.post<Activity>(`/sales/tenants/${tenantId}/activities`, input)
  return data
}
